import React from 'react';
import { applySpelling } from '@/utils/spelling';
import { useRegion } from '@/contexts/RegionContext';
import { motion } from 'motion/react';
import { SEOHead } from '@/components/shared/SEOHead';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Award, BookOpen, Users, CheckCircle2, ArrowRight, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegionalPageContent } from '@/hooks/useRegionalPageContent';
import { LazyImage } from '@/components/ui/LazyImage';


export function PractitionersPage() {
  const { regionCode } = useRegion();
  const s = (text: string) => applySpelling(text, regionCode);
  const { t } = useTranslation();
  const { content: customContent, loading: customLoading } = useRegionalPageContent('practitioners');

  const headline = customContent?.title || t('prac.headline', 'Equip yourself to develop others');
  const description = t('prac.desc', 'The FIVE LENS Enneagram is designed for coaches, OD practitioners, and HR leaders who require a rigorous, multidimensional approach to human development.');

  const getRegionalBlock = () => {
    switch(regionCode) {
      case 'za':
        return {
          title: "South Africa's Leading Framework",
          p1: "Born in Cape Town and developed over three decades, the FIVE LENS Enneagram has become the standard for corporate coaching and team development across Southern Africa.",
          p2: "As a certified practitioner, you gain access to a framework that deeply understands the local corporate context while maintaining global validity standards."
        };
      case 'gb':
      case 'eu':
        return {
          title: "European Coaching Excellence",
          p1: "Join a growing network of elite European practitioners utilizing the FIVE LENS framework to drive transformation in multinational organizations.",
          p2: "Fully compliant with GDPR and aligned with ICF European chapters, our certification ensures you meet the highest standards of professional coaching."
        };
      case 'us':
        return {
          title: "Advanced Practitioner Certification",
          p1: "Stand out in the North American coaching market by moving beyond standard single-typology assessments.",
          p2: "The FIVE LENS framework provides the multidimensional data required by today's Fortune 500 HR leaders and executive sponsors."
        };
      default:
        return {
          title: "A Multidimensional Approach",
          p1: "Most personality frameworks measure only a single dimension of human behavior. The FIVE LENS Enneagram integrates five distinct psychological lenses, providing an unprecedented depth of insight.",
          p2: "As a certified practitioner, you are equipped to move past surface-level symptoms and address the root causes of individual limiting beliefs and team friction, delivering measurable ROI for corporate clients."
        };
    }
  };

  const regionalBlock = getRegionalBlock();

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <SEOHead />
      
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: t('nav.practitioners', 'For Coaches and Practitioners') }
        ]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-3xl">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-[1px] w-8 bg-rule-3"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">
                {t('prac.eyebrow', 'Professional Certification')}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
              {headline}
            </h1>
            
            {customContent?.htmlContent ? (
              <div 
                className="prose prose-brand max-w-none prose-p:text-xl prose-p:font-light prose-p:text-ink/70/80 prose-p:mb-12 prose-p:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: customContent.htmlContent }}
              />
            ) : (
              <p className="text-xl font-light text-ink/70/80 mb-12 leading-relaxed">
                {description}
              </p>
            )}
          </div>

          <div className="mb-24 w-full h-[500px] overflow-hidden bg-ground-deep relative">
            <div className="absolute inset-0 bg-ink/20 mix-blend-multiply z-10" />
            <LazyImage 
              src="/five_lens_practitioner_certification_web.webp"
              alt="Professional coaching session"
              className="w-full h-full object-cover relative z-0"
              referrerPolicy="no-referrer"
            />
          </div>

          <div className="grid lg:grid-cols-12 gap-16 mb-24">
            <div className="lg:col-span-4">
              <h2 className="text-3xl font-serif text-ink mb-6 leading-tight">
                {regionalBlock.title}
              </h2>
              <div className="h-1 w-12 bg-rule-3 mb-6"></div>
              <Link 
                to="/find-a-practitioner"
                className="inline-flex mt-4 items-center justify-center bg-transparent border border-rule-3 text-ink px-6 py-3 uppercase tracking-widest text-xs font-bold hover:bg-rule-3 hover:text-white transition-colors"
              >
                Find a Practitioner
              </Link>
            </div>
            <div className="lg:col-span-8 prose prose-lg prose-brand max-w-none text-ink/70/80 leading-relaxed font-light">
              <p className="mb-6">
                {regionalBlock.p1}
              </p>
              <p>
                {regionalBlock.p2}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-24">
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group">
              <Award className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">ICF Accredited</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                Our certification programs are fully accredited by the International Coach Federation (ICF), providing Continuing Coach Education (CCE) hours.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Earn CCE credits</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Rigorous academic backing</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> High construct validity</li>
              </ul>
            </div>
            
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group transform md:-translate-y-4">
              <BookOpen className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Comprehensive Toolkit</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                Gain full access to the proprietary assessment platform, detailed individual and team dynamics reports, and advanced coaching debrief guides.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Co-branded PDF reports</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Practitioner dashboard</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Automated billing features</li>
              </ul>
            </div>
            
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group">
              <Users className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Global Community</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                Join a network of over 700 certified professionals worldwide. Benefit from ongoing supervision, advanced modules, and peer collaboration.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Masterclass webinars</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Peer supervision groups</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Marketing support assets</li>
              </ul>
            </div>
          </div>

          <div className="mb-24">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-serif text-ink mb-6">Who Should Get Certified?</h2>
                <p className="text-lg text-ink/70/80 font-light mb-8">
                  The FIVE LENS certification is designed for professionals dedicated to unlocking human potential and facilitating deep organizational change. Our cohorts typically include:
                </p>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white border border-ground-deep flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-rule-3" />
                    </div>
                    <div>
                      <h4 className="font-bold text-ink mb-1">Executive & Leadership Coaches</h4>
                      <p className="text-sm text-ink/70/70">Move beyond behavioral coaching to address the root causes of executive derailment and facilitate profound vertical development.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white border border-ground-deep flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-rule-3" />
                    </div>
                    <div>
                      <h4 className="font-bold text-ink mb-1">OD & HR Professionals</h4>
                      <p className="text-sm text-ink/70/70">Implement a scalable, unified language for talent development, team building, and cultural transformation across the enterprise.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white border border-ground-deep flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-rule-3" />
                    </div>
                    <div>
                      <h4 className="font-bold text-ink mb-1">Consultants & Facilitators</h4>
                      <p className="text-sm text-ink/70/70">Equip your practice with robust, data-driven diagnostics that provide immediate value and measurable ROI to your clients.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-ground-deep/30 p-8 md:p-12 relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-rule-3/20 -mt-4 -mr-4 -z-10"></div>
                <h3 className="text-2xl font-serif text-ink mb-6">The Platform Advantage</h3>
                <p className="text-sm text-ink/70/80 leading-relaxed mb-8">
                  Certification grants you access to our proprietary assessment platform, designed specifically for the needs of professional practitioners.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rule-3 flex-shrink-0" />
                    <span className="text-sm text-ink/70/80"><strong>White-Labeling:</strong> Add your own logo and branding to the final PDF reports generated for your clients.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rule-3 flex-shrink-0" />
                    <span className="text-sm text-ink/70/80"><strong>Team Dynamics Mapping:</strong> Automatically aggregate individual profiles into comprehensive team reports to identify collective blind spots.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rule-3 flex-shrink-0" />
                    <span className="text-sm text-ink/70/80"><strong>Client Management:</strong> Send assessments, track completion status, and manage your credits all from a centralized dashboard.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rule-3 flex-shrink-0" />
                    <span className="text-sm text-ink/70/80"><strong>Multi-Language Support:</strong> Generate reports in multiple languages to support global and diverse teams.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-24 border-t border-ground-deep pt-24">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-serif text-ink mb-6">The Certification Journey</h2>
              <p className="text-lg font-light text-ink/70/80">
                Our blended learning approach combines the flexibility of self-paced digital modules with the depth of interactive, faculty-led cohort sessions.
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white border border-ground-deep p-6 relative">
                <span className="absolute -top-3 -left-3 w-8 h-8 bg-rule-3 text-white flex items-center justify-center font-bold text-sm shadow-sm">01</span>
                <h4 className="font-bold text-ink mt-4 mb-3">Self-Discovery</h4>
                <p className="text-sm text-ink/70/70">Complete your own FIVE LENS assessment and receive a comprehensive 1-on-1 debrief with a Master Practitioner.</p>
              </div>
              <div className="bg-white border border-ground-deep p-6 relative">
                <span className="absolute -top-3 -left-3 w-8 h-8 bg-ink text-white flex items-center justify-center font-bold text-sm shadow-sm">02</span>
                <h4 className="font-bold text-ink mt-4 mb-3">Foundational Theory</h4>
                <p className="text-sm text-ink/70/70">{s("Complete the self-paced e-learning modules covering the Enneagram, Social Drives, Energy Centres, and Resilience.")}</p>
              </div>
              <div className="bg-white border border-ground-deep p-6 relative">
                <span className="absolute -top-3 -left-3 w-8 h-8 bg-rule-3 text-white flex items-center justify-center font-bold text-sm shadow-sm">03</span>
                <h4 className="font-bold text-ink mt-4 mb-3">Cohort Intensives</h4>
                <p className="text-sm text-ink/70/70">Participate in live, interactive virtual sessions to practice debriefing strategies and analyze complex case studies.</p>
              </div>
              <div className="bg-white border border-ground-deep p-6 relative">
                <span className="absolute -top-3 -left-3 w-8 h-8 bg-ink text-white flex items-center justify-center font-bold text-sm shadow-sm">04</span>
                <h4 className="font-bold text-ink mt-4 mb-3">Supervised Practice</h4>
                <p className="text-sm text-ink/70/70">Conduct practice debriefs with your own clients, receiving detailed feedback and supervision to ensure readiness.</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-ink via-ink/80 to-ink/90 text-ground p-10 md:p-16 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-serif mb-4 text-white">Become a Certified Practitioner</h2>
              <p className="text-white text-lg mb-8 max-w-xl font-light">
                The full accreditation process combines self-paced theoretical modules with intensive, interactive cohort sessions. Apply today to secure your place in the next cohort.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/practitioner-certification"
                  className="inline-flex items-center justify-center bg-rule-3 text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-ink transition-colors gap-2"
                >
                  View Certification Options <ArrowRight className="w-4 h-4" />
                </Link>
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center border border-white text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-white/10 transition-colors"
                >
                  Contact Admissions
                </Link>
              </div>
            </div>
            <Award className="absolute -bottom-8 -right-8 w-80 h-80 text-white/5 pointer-events-none" />
          </div>
        </motion.div>
      </div>
    </main>
  );
}
